import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Group,
  List,
  Loader,
  SegmentedControl,
  Stack,
  Tabs,
  Text,
  TextInput,
  useMantineColorScheme,
  useMantineTheme
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import React, { useEffect, useState } from 'react';
import { CVItemCard } from '../../../../../components/common/CVItemCard';
import { CVSectionContainer } from '../../../../../components/common/CVSectionContainer';
import { DeleteConfirmationModal } from '../../../../../components/common/DeleteConfirmationModal';
import { MonthYearInput } from '../../../../../components/common/MonthYearInput';
import { ValidationResult } from '../../../../../components/common/ValidationResult';
import { FormUIColor } from '../../../../../constants/colorPalette';
import { useEditMode } from "../../../../../context/EditModeContext";
import { ParticipationType } from '../../../../../models/course.model';
import { CoursesService } from '../../../../../services/coursesService';
import { Course } from '../../../../../types/cv';
import { ValidateableCvSections } from '../../../../../types/cvSections';
import { createDateString, parseDateString, toUTCDate } from '../../../../../utils/dateFormat';

const INSTRUCTIONS = (
  <Stack gap={5}>
    <Text fw={500}>Instructions from your company</Text>
    <Text size="sm">This section includes both courses you have attended and training sessions you have conducted:</Text>
    <Text size="sm">• Attended: Courses that you have participated in, externally and internally.</Text>
    <Text size="sm">• Conducted: Presentations, speeches and training sessions you have delivered.</Text>
  </Stack>
);

interface TrainingCoursesProps {
  courses: Course[];
  onChange: (courses: Course[]) => void;
  userId?: number;
  cvId?: number;
}

export function TrainingCourses({ courses, onChange, userId, cvId }: TrainingCoursesProps) {
  const { isEditMode } = useEditMode();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [editingCourseId, setEditingCourseId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>("attended");

  // Function to format training courses data for LLM validation
  const getTrainingCoursesSectionData = (): string => {
    if (!courses || courses.length === 0) {
      return 'No training courses found.';
    }

    return courses.map((course, index) => {
      const participationType = course.participationType === ParticipationType.Attended ? 'Attended' : 'Conducted';
      return `
Course ${index + 1}:
- Name: ${course.name}
- Year Completed: ${course.yearCompleted}
- Participation Type: ${participationType}
      `.trim();
    }).join('\n\n');
  };

  const [newCourse, setNewCourse] = useState<Partial<Course> & { month?: string; year?: string }>({
    name: '',
    yearCompleted: '',
    participationType: ParticipationType.Attended,
    month: '',
    year: ''
  });

  const [editCourse, setEditCourse] = useState<Course & { month?: string; year?: string }>({
    id: '',
    name: '',
    yearCompleted: '',
    includeInCV: true,
    participationType: ParticipationType.Attended
  });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<Course | null>(null);
  const coursesService = new CoursesService();
  const [showAddValidation, setShowAddValidation] = useState(false);
  const [showEditValidation, setShowEditValidation] = useState(false);

  // Load courses from the service if userId is provided
  useEffect(() => {
    if (userId && isEditMode) {
      loadCourses();
    }
  }, [userId, isEditMode]);

  // Close and reset all forms when edit mode is disabled
  useEffect(() => {
    if (!isEditMode) {
      setIsAddFormOpen(false);
      setEditingCourseId(null);
      setNewCourse({
        name: '',
        yearCompleted: '',
        participationType: ParticipationType.Attended,
        month: '',
        year: ''
      });
      setDeleteModalOpen(false);
      setShowAddValidation(false);
      setShowEditValidation(false);
    }
  }, [isEditMode]);

  const loadCourses = async () => {
    if (!userId) return;

    setIsLoading(true);
    try {
      const data = await coursesService.getCourseListByPersonId(userId);

      // Transform service data to match Course type
      const transformedCourses: Course[] = data.map(item => ({
        id: item.id.toString(),
        name: item.title,
        yearCompleted: new Date(item.date).getFullYear().toString(),
        includeInCV: item.isStarred,
        participationType: item.participationType
      }));

      onChange(transformedCourses);
    } catch (error) {
      console.error('Error loading courses:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load courses. Please try again.',
        color: 'red'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: string, value: any) =>
    setNewCourse(prev => ({ ...prev, [field]: value }));

  const handleEditChange = (field: string, value: any) =>
    setEditCourse(prev => ({ ...prev, [field]: value }));

  const handleAddCourse = async () => {
    setShowAddValidation(true);
    if (!newCourse.name) return;
    if (activeTab === 'attended' && !newCourse.yearCompleted) return;
    if (activeTab === 'conducted' && (!newCourse.month || !newCourse.year)) return;

    // Use month/year if provided (for conducted), otherwise just use year (for attended)
    let yearCompleted = newCourse.yearCompleted || '';
    if (newCourse.participationType === ParticipationType.Conducted && newCourse.month && newCourse.year) {
      yearCompleted = createDateString(newCourse.month, newCourse.year);
    }

    const courseEntry: Course = {
      id: Date.now().toString(),
      name: newCourse.name,
      yearCompleted: yearCompleted,
      includeInCV: true,
      participationType: newCourse.participationType
    };

    if (userId) {
      setIsSaving(true);
      try {
        // Create the service model
        const year = parseInt(newCourse.year || newCourse.yearCompleted || new Date().getFullYear().toString());
        const month = parseInt(newCourse.month || '0');
        const courseDate = toUTCDate(new Date(year, month, 1));

        const serviceData = {
          id: 0, // Will be assigned by the server
          userID: userId,
          title: newCourse.name,
          date: courseDate,
          isStarred: true,
          participationType: newCourse.participationType || ParticipationType.Attended
        };

        const result = await coursesService.addCourseItem(serviceData);

        // Update with the ID from the server
        courseEntry.id = result.id.toString();

        onChange([...courses, courseEntry]);
        notifications.show({
          title: 'Success',
          message: 'Course added successfully',
          color: 'green'
        });
      } catch (error) {
        console.error('Error adding course:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to add course. Please try again.',
          color: 'red'
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      // Just update local state if userId is not provided (fallback)
      onChange([...courses, courseEntry]);
    }

    setNewCourse({
      name: '',
      yearCompleted: '',
      participationType: newCourse.participationType,
      month: '',
      year: ''
    });
    setIsAddFormOpen(false);
  };

  const handleRemoveCourse = async (id: string) => {
    if (userId) {
      const courseToDelete = courses.find(course => course.id === id);
      if (!courseToDelete) return;

      setIsSaving(true);
      try {
        const year = parseInt(courseToDelete.yearCompleted || new Date().getFullYear().toString());

        await coursesService.deleteCourseItem({
          id: parseInt(courseToDelete.id),
          userID: userId,
          title: courseToDelete.name,
          date: toUTCDate(new Date(year, 0, 1)),
          isStarred: courseToDelete.includeInCV,
          participationType: courseToDelete.participationType || ParticipationType.Attended
        });

        onChange(courses.filter(course => course.id !== id));
        notifications.show({
          title: 'Success',
          message: 'Item deleted successfully',
          color: 'green'
        });
      } catch (error) {
        console.error('Error deleting course:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to delete item. Please try again.',
          color: 'red'
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      // Just update local state if userId is not provided (fallback)
      onChange(courses.filter(course => course.id !== id));
    }
  };

  const handleToggleInclude = async (id: string) => {
    const courseToUpdate = courses.find(course => course.id === id);
    if (!courseToUpdate) return;

    const updatedIncludeStatus = !courseToUpdate.includeInCV;

    if (userId) {
      setIsSaving(true);
      try {
        const year = parseInt(courseToUpdate.yearCompleted || new Date().getFullYear().toString());

        await coursesService.updateCourseItem({
          id: parseInt(courseToUpdate.id),
          userID: userId,
          title: courseToUpdate.name,
          date: toUTCDate(new Date(year, 0, 1)),
          isStarred: updatedIncludeStatus,
          participationType: courseToUpdate.participationType || ParticipationType.Attended
        });

        onChange(courses.map(course =>
          course.id === id ? { ...course, includeInCV: updatedIncludeStatus } : course
        ));
      } catch (error) {
        console.error('Error updating course:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to update item. Please try again.',
          color: 'red'
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      // Just update local state if userId is not provided (fallback)
      onChange(courses.map(course =>
        course.id === id ? { ...course, includeInCV: updatedIncludeStatus } : course
      ));
    }
  };

  const handleEditCourse = (course: Course) => {
    // For conducted courses, parse the date
    let month = '';
    let year = '';

    if (course.participationType === ParticipationType.Conducted) {
      const parsed = parseDateString(course.yearCompleted);
      month = parsed.month;
      year = parsed.year;
    } else {
      year = course.yearCompleted;
    }

    setEditingCourseId(course.id);
    setEditCourse({
      ...course,
      month,
      year
    });
  };

  const saveCourseEdit = async () => {
    setShowEditValidation(true);
    if (!editingCourseId || !editCourse.name) return;
    if (editCourse.participationType === ParticipationType.Attended && !editCourse.yearCompleted) return;
    if (editCourse.participationType === ParticipationType.Conducted && (!editCourse.month || !editCourse.year)) return;

    // Use month/year if provided (for conducted), otherwise just use year (for attended)
    let yearCompleted = editCourse.yearCompleted;
    if (editCourse.participationType === ParticipationType.Conducted && editCourse.month && editCourse.year) {
      yearCompleted = createDateString(editCourse.month, editCourse.year);
    }

    const updatedCourse: Course = {
      ...editCourse,
      yearCompleted,
    };

    if (userId) {
      setIsSaving(true);
      try {
        // Prepare service data
        const year = parseInt(editCourse.year || editCourse.yearCompleted || new Date().getFullYear().toString());
        const month = parseInt(editCourse.month || '0');

        await coursesService.updateCourseItem({
          id: parseInt(editCourse.id),
          userID: userId,
          title: editCourse.name,
          date: toUTCDate(new Date(year, month, 1)),
          isStarred: editCourse.includeInCV,
          participationType: editCourse.participationType || ParticipationType.Attended
        });

        onChange(courses.map(course =>
          course.id === editingCourseId ? updatedCourse : course
        ));

        notifications.show({
          title: 'Success',
          message: 'Item updated successfully',
          color: 'green'
        });
      } catch (error) {
        console.error('Error updating course:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to update item. Please try again.',
          color: 'red'
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      // Just update local state if userId is not provided (fallback)
      onChange(courses.map(course =>
        course.id === editingCourseId ? updatedCourse : course
      ));
    }

    setEditingCourseId(null);
    setShowEditValidation(false);
  };

  const openDeleteConfirmation = (course: Course) => {
    setCourseToDelete(course);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (courseToDelete) {
      handleRemoveCourse(courseToDelete.id);
      setDeleteModalOpen(false);
      setCourseToDelete(null);
    }
  };

  // Filter courses by participation type
  const attendedCourses = courses.filter(course =>
    course.participationType === ParticipationType.Attended || !course.participationType
  );

  const conductedCourses = courses.filter(course =>
    course.participationType === ParticipationType.Conducted
  );

  const handleTabChange = (value: string | null) => {
    setActiveTab(value);
    setNewCourse(prev => ({
      ...prev,
      participationType: value === "conducted" ? ParticipationType.Conducted : ParticipationType.Attended,
      month: '',
      year: ''
    }));
  };

  return (
    <CVSectionContainer
      sectionId="training-courses"
      title="Training and Courses"
      buttonText={isEditMode ? "Add Course" : undefined}
      onAddClick={isEditMode ? () => setIsAddFormOpen(true) : undefined}
      instructions={isEditMode ? INSTRUCTIONS : undefined}
      personId={userId}
      cvId={cvId}
      getSectionData={getTrainingCoursesSectionData}
    >
      <ValidationResult section={ValidateableCvSections.Trainings} />

      <Collapse in={isAddFormOpen}>
        <Box mb="md" p="md" style={{ border: `2px solid ${theme.colors.cyan[7]}`, borderRadius: theme.radius.sm, backgroundColor: isDark ? theme.colors.dark[6] : FormUIColor.ADD_FORM_BG }}>
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Text fw={600} size="sm">Add new entry</Text>
              <SegmentedControl
                size="xs"
                value={activeTab ?? 'attended'}
                onChange={(value) => handleTabChange(value)}
                data={[
                  { label: 'Attended Course', value: 'attended' },
                  { label: 'Conducted Training', value: 'conducted' },
                ]}
              />
            </Group>
            {activeTab === 'attended' ? (
              <Text size="xs" c="dimmed">A course, certification or learning programme you participated in.</Text>
            ) : (
              <Text size="xs" c="dimmed">A presentation, speech or training session you personally delivered.</Text>
            )}
            <TextInput
              label="Title"
              placeholder={activeTab === "attended" ? "Course title" : "Title of training or presentation"}
              value={newCourse.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />

            {activeTab === "attended" ? (
              <>
                <TextInput
                  label="Year Completed"
                  placeholder="e.g. 2023"
                  value={newCourse.yearCompleted}
                  onChange={(e) => handleChange('yearCompleted', e.target.value)}
                  required
                />
              </>
            ) : (
              <MonthYearInput
                label="Date"
                monthValue={newCourse.month || ''}
                yearValue={newCourse.year || ''}
                onMonthChange={(value) => handleChange('month', value)}
                onYearChange={(value) => handleChange('year', value)}
                required
              />
            )}

            <Group justify="flex-end" gap="sm">
              {showAddValidation && (() => {
                const errors: string[] = [];
                if (!newCourse.name) errors.push('Title is required');
                if (activeTab === 'attended' && !newCourse.yearCompleted) errors.push('Year completed is required');
                if (activeTab === 'conducted' && (!newCourse.month || !newCourse.year)) errors.push('Date (month and year) is required');
                return errors.length > 0 ? (
                  <Alert color="red" w="100%" py="xs">
                    <List size="sm" spacing={2}>
                      {errors.map((e) => <List.Item key={e}>{e}</List.Item>)}
                    </List>
                  </Alert>
                ) : null;
              })()}
              <Button
                variant="outline"
                leftSection={<XMarkIcon width={16} height={16} />}
                onClick={() => { setIsAddFormOpen(false); setShowAddValidation(false); }}
              >
                Cancel
              </Button>
              <Button
                leftSection={<CheckIcon width={16} height={16} />}
                onClick={handleAddCourse}
                loading={isSaving}
              >
                {activeTab === 'conducted' ? 'Add Training' : 'Add Course'}
              </Button>
            </Group>
          </Stack>
        </Box>
      </Collapse>

      {isLoading ? (
        <Box style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
          <Loader size="md" />
        </Box>
      ) : (
        <Tabs defaultValue="attended" value={activeTab} onChange={handleTabChange}>
          <Tabs.List mb="md">
            <Tabs.Tab value="attended">
              Attended Courses ({attendedCourses.length})
            </Tabs.Tab>
            <Tabs.Tab value="conducted">
              Conducted Training ({conductedCourses.length})
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="attended">
            {attendedCourses.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">No attended courses added yet.</Text>
            ) : (
              attendedCourses.map((course) => (
                <React.Fragment key={course.id}>
                  {editingCourseId === course.id ? (
                    // Show edit form instead of the card when editing
                    <Box mb="md" p="md" style={{ border: `2px solid ${theme.colors.blue[5]}`, borderRadius: theme.radius.sm, backgroundColor: isDark ? theme.colors.dark[6] : theme.colors.blue[0] }}>
                      <Text size="sm" c="blue" fw={500} mb="md">Editing: {course.name}</Text>
                      <Stack gap="md">
                        <TextInput
                          label="Title"
                          placeholder="Course title"
                          value={editCourse.name}
                          onChange={(e) => handleEditChange('name', e.target.value)}
                          required
                        />

                        <TextInput
                          label="Year Completed"
                          placeholder="e.g. 2023"
                          value={editCourse.yearCompleted}
                          onChange={(e) => handleEditChange('yearCompleted', e.target.value)}
                          required
                        />

                        <Group justify="flex-end" gap="sm">
                          {showEditValidation && (() => {
                            const errors: string[] = [];
                            if (!editCourse.name) errors.push('Title is required');
                            if (!editCourse.yearCompleted) errors.push('Year completed is required');
                            return errors.length > 0 ? (
                              <Alert color="red" w="100%" py="xs">
                                <List size="sm" spacing={2}>
                                  {errors.map((e) => <List.Item key={e}>{e}</List.Item>)}
                                </List>
                              </Alert>
                            ) : null;
                          })()}
                          <Button
                            variant="outline"
                            leftSection={<XMarkIcon width={16} height={16} />}
                            onClick={() => { setEditingCourseId(null); setShowEditValidation(false); }}
                          >
                            Cancel
                          </Button>
                          <Button
                            leftSection={<CheckIcon width={16} height={16} />}
                            onClick={saveCourseEdit}
                            loading={isSaving}
                          >
                            Save
                          </Button>
                        </Group>
                      </Stack>
                    </Box>
                  ) : (
                    // Show the normal card when not editing
                    <CVItemCard
                      title={course.name}
                      dates={course.yearCompleted}
                      included={course.includeInCV}
                      onEdit={() => handleEditCourse(course)}
                      onDelete={() => openDeleteConfirmation(course)}
                      onToggleInclude={() => handleToggleInclude(course.id)}
                      isEditMode={isEditMode}
                    />
                  )}
                </React.Fragment>
              ))
            )}
          </Tabs.Panel>

          <Tabs.Panel value="conducted">
            {conductedCourses.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">No conducted training sessions added yet.</Text>
            ) : (
              conductedCourses.map((course) => (
                <React.Fragment key={course.id}>
                  {editingCourseId === course.id ? (
                    // Show edit form instead of the card when editing
                    <Box mb="md" p="md" style={{ border: `2px solid ${theme.colors.blue[5]}`, borderRadius: theme.radius.sm, backgroundColor: isDark ? theme.colors.dark[6] : theme.colors.blue[0] }}>
                      <Text size="sm" c="blue" fw={500} mb="md">Editing: {course.name}</Text>
                      <Stack gap="md">
                        <TextInput
                          label="Title"
                          placeholder="Title of training or presentation"
                          value={editCourse.name}
                          onChange={(e) => handleEditChange('name', e.target.value)}
                          required
                        />

                        <MonthYearInput
                          label="Date"
                          monthValue={editCourse.month || ''}
                          yearValue={editCourse.year || ''}
                          onMonthChange={(value) => handleEditChange('month', value)}
                          onYearChange={(value) => handleEditChange('year', value)}
                          required
                        />

                        <Group justify="flex-end" gap="sm">
                          {showEditValidation && (() => {
                            const errors: string[] = [];
                            if (!editCourse.name) errors.push('Title is required');
                            if (!editCourse.month || !editCourse.year) errors.push('Date (month and year) is required');
                            return errors.length > 0 ? (
                              <Alert color="red" w="100%" py="xs">
                                <List size="sm" spacing={2}>
                                  {errors.map((e) => <List.Item key={e}>{e}</List.Item>)}
                                </List>
                              </Alert>
                            ) : null;
                          })()}
                          <Button
                            variant="outline"
                            leftSection={<XMarkIcon width={16} height={16} />}
                            onClick={() => { setEditingCourseId(null); setShowEditValidation(false); }}
                          >
                            Cancel
                          </Button>
                          <Button
                            leftSection={<CheckIcon width={16} height={16} />}
                            onClick={saveCourseEdit}
                            loading={isSaving}
                          >
                            Save
                          </Button>
                        </Group>
                      </Stack>
                    </Box>
                  ) : (
                    // Show the normal card when not editing
                    <CVItemCard
                      title={course.name}
                      dates={course.yearCompleted}
                      included={course.includeInCV}
                      onEdit={() => handleEditCourse(course)}
                      onDelete={() => openDeleteConfirmation(course)}
                      onToggleInclude={() => handleToggleInclude(course.id)}
                      isEditMode={isEditMode}
                    />
                  )}
                </React.Fragment>
              ))
            )}
          </Tabs.Panel>
        </Tabs>
      )}

      <DeleteConfirmationModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        itemName={courseToDelete ? courseToDelete.name : undefined}
      />
    </CVSectionContainer>
  );
} 
